/* ═══════════════════════════════════════════════════════════════
   THE MODULE BUILDER (docs/MODULES_R2_PLAN.md Phase 4).

   ⛔ A MODULE is a reusable block-group, single-spec only — never a variant.

   This is the SPEC STUDIO CANVAS in module mode, not a second editor. The
   same `SpecStudioCanvas`, the same TipTap schema, the same numbering brain,
   the same gutter grips, the same right-click menu, the same toolbar
   clusters — module mode is a PROP (`moduleBuild`), so nothing here can
   drift away from how a spec feels to type in.

   What module mode adds is Ruling 6, the module structure law:
     · every level-2 article declares its PART (1/2/3) — set by dragging it
       among the three rendered PART headers (Cohesion R1 Phase 1) or from
       the right-click menu's "Part" picker, because only the part number is
       an address when the module lands (`*.##`).
     · every paragraph lives under an article. Breaking that raises an INLINE
       FLAG on the offending paragraph and stands the Save button down — never
       an error dialog.

   MASTERING (Ruling 1): PUBLISHING here moves the LIBRARY definition — future
   inserts get it, and every copy already out there is offered the update.
   Editing a module inside a spec only ever changes that spec's copy. The
   builder says so, quietly, once.

   SYNC D14: the definition is edited in a SHARED DRAFT (a live Yjs document,
   `useModuleDraft`) — Published / Pending publish / Saving… / Offline on one
   chip, peers beside it, Publish (modules.publish) moves the draft to the
   library, Discard draft drops it for everyone. The draft is server-held, so
   leaving the builder never loses it. Name / description / category / tags are
   details, not the draft, and keep their own direct "Save details" path.

   COHESION R1 PHASE 2: the editor gains real parity with the Spec Studio's
   own dock — the Blocks toolbox (insert block/table/image, level 2–9 only:
   parts are the fixed pseudo-structure), the shared ZoomControl + ctrl+wheel,
   a paragraph-numbers view toggle, "Save as new module", and a Studio-style
   three-way exit guard. What stays OUT on purpose (verified, not assumed):
   variants/variant anything, the Updates panel, module-in-module nesting,
   Publish/Submit-for-review, Track changes — none of that is mounted here.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, ArrowLeft, Check } from 'lucide-react';
import { Icon } from '../components/ui/Icon';
import { undoDepth } from '@tiptap/pm/history';
import type { Editor } from '@tiptap/react';
import type { JSONContent } from '@tiptap/core';
import type { ContentBlock } from '../../shared/types';
import { hasPermission } from '../../shared/permissions';
import {
  moduleBlocksFromChunks,
  moduleChunksFromBlocks,
  moduleViolationsByBlock,
  validateModuleStructure,
  type ModuleChunk,
  type ModuleTag,
  type ModuleVersion,
  type SpecModule,
} from '../../shared/spec-modules';
import { ModuleService } from '../services/moduleService';
import { CategoriesService } from '../services/categoriesService';
import { useTableSync } from '../hooks/useTableSync';
import type { ModuleCategory } from '../../shared/module-categories';
import { tagSuggestionsFrom } from '../../shared/module-tags';
import { ModuleCategorySelect } from '../components/ModuleCategorySelect';
import { ModuleTagsInput } from '../components/ModuleTagsInput';
import { reportError } from '../services/errorLog';
import { Button } from '../components/ui/button';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { EditorHistoryPanel } from '../components/EditorComponents';
import { ZoomControl } from '../components/ui/ZoomControl';
import { FormaDock, type FormaDockHandle, type PaletteDef } from '../components/FormaDock';
import {
  DesignerToolbar,
  ToolbarButton,
  type PanelEntry,
  type ToolbarItem,
} from '../editor-chrome/Toolbar';
import { usePref } from '../lib/usePref';
import { InlineMarks, LevelCluster, UniversalHistory } from '../editor-chrome/ToolbarControls';
import { FindReplace } from '../designer/FindReplace';
import { PanelDropdown } from '../editor-chrome/PanelDropdown';
import { ToolPanel } from '../editor-chrome/ToolPanel';
import { isOverDockZone, type FloatingTool } from '../editor-chrome/dock';
import { useUndoCoordinator } from '../editor-chrome/undoCoordinator';
import { newBlockId } from '../designer/schema';
import { SpecStudioCanvas } from './SpecStudioCanvas';
import { PeerChips } from './PeerChips';
import { usePublishWalk } from '../review/usePublishWalk';
import { ReviewBar } from '../review/ReviewBar';
import { ReviewPanel } from '../review/ReviewPanel';
import { useModuleDraft } from './useModuleDraft';
import { liveBlockMessage } from './collabState';
import {
  canPublishModule,
  moduleChipLabel,
  moduleDiscardBody,
  modulePublishBody,
  moduleSeedChunks,
  type ModuleChipState,
} from './moduleDraftState';
import { BlocksPalette, SPEC_STUDIO_PANEL_ICONS } from './SpecStudioWorkspace';
import '../editor-chrome/editor.css';
import './Studio.css';

const ZOOM_MIN_PCT = 50;
const ZOOM_MAX_PCT = 180;

/** The module editor's own dock — canvas plus the Blocks toolbox only
 *  (Cohesion R1 Phase 2 item 1). No Outline/Updates/Modules panels: nothing
 *  variant- or review-shaped belongs in a module definition. */
const MODULE_PALETTES: PaletteDef[] = [
  { id: 'canvas', title: 'Document', closable: false, noTab: true },
  {
    id: 'blocks',
    title: 'Blocks',
    width: 288,
    defaultPosition: { direction: 'right', reference: 'canvas' },
  },
];
const titleOf = (id: string) => MODULE_PALETTES.find((p) => p.id === id)?.title || id;

/** What the builder was opened WITH. Exactly one of `module` / `seedChunks`
 *  is the content; both may be present when a spec selection is about to
 *  become a new version of a module that already exists. */
export interface ModuleBuilderDraft {
  /** The library row being mastered. Null ⇒ a module that does not exist yet. */
  module?: SpecModule | null;
  /** Content to start from — the chunks a spec selection just produced.
   *  Absent ⇒ start from `module.chunks`, or from one empty article. */
  seedChunks?: ModuleChunk[] | null;
  name?: string;
  description?: string;
  /** Migration 056 — seeded from "Save as module…" when it is given there;
   *  otherwise read off `module` for an existing row. */
  categoryId?: string | null;
  tags?: string[];
  sourceSpecId?: string;
  sourceCsi?: string;
}

export interface ModuleBuilderProps {
  draft: ModuleBuilderDraft;
  userName: string;
  userId?: string;
  isDark?: boolean;
  userPermissions: string[];
  toast: (msg: string, isError?: boolean) => void;
  onClose: () => void;
  /** A save landed — the library reloads on this. */
  onSaved?: (module: SpecModule) => void;
}

const mintId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `mb-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;

/** A brand-new module opens on one empty article, so the first thing the
 *  writer sees is the shape a module has — not a blank page. */
const blankModuleBlocks = (): ContentBlock[] => [
  { id: newBlockId(), level: 2, text: '' } as ContentBlock,
];

/** After a module write: make sure its tags exist in the firm's tag catalog
 *  (migration 060). Runs after the module is already saved, so a failure is
 *  logged, not surfaced as a failed save. */
function ensureTagsInCatalog(tags: string[], userName: string): void {
  if (!tags.length) return;
  void ModuleService.ensureTagsInCatalog(tags, userName).catch((err) =>
    console.error('[ModuleBuilder] tags could not be added to the catalog', err)
  );
}

/** The module's one save chip — the same look as the Spec Studio's. */
function ModuleDraftChip({ state }: { state: ModuleChipState }) {
  const tone =
    state === 'published'
      ? 'clean'
      : state === 'saving' || state === 'connecting'
        ? 'saving'
        : 'dirty';
  return (
    <span className={`dsg-save dsg-save-${tone}`} role="status" aria-live="polite">
      {state === 'published' && <Icon icon={Check} em={1.0909} aria-hidden="true" />}
      {moduleChipLabel(state)}
    </span>
  );
}

export function ModuleBuilder({
  draft,
  userName,
  userId,
  isDark = false,
  userPermissions,
  toast,
  onClose,
  onSaved,
}: ModuleBuilderProps) {
  // The row this builder is mastering, as it stood at the last successful
  // save. The version bump is measured from HERE, so one save moves the
  // version exactly once no matter how many times Ctrl+S is pressed.
  const [baseline, setBaseline] = React.useState<SpecModule | null>(draft.module || null);
  const [name, setName] = React.useState(draft.name ?? draft.module?.name ?? '');
  const [description, setDescription] = React.useState(
    draft.description ?? draft.module?.description ?? ''
  );
  // Migration 056 — metadata only, never bumps the version on its own
  // (see `metaOnlyChanged` below and `ModuleService.updateMeta`).
  const [categoryId, setCategoryId] = React.useState<string | null>(
    draft.categoryId !== undefined ? draft.categoryId : (draft.module?.categoryId ?? null)
  );
  const [tags, setTags] = React.useState<string[]>(draft.tags ?? draft.module?.tags ?? []);
  const [metaError, setMetaError] = React.useState<string | null>(null);
  const [categories, setCategories] = React.useState<ModuleCategory[]>([]);
  const [allModules, setAllModules] = React.useState<SpecModule[]>([]);
  const [tagCatalog, setTagCatalog] = React.useState<ModuleTag[]>([]);
  React.useEffect(() => {
    void CategoriesService.listAll().then(setCategories);
    void ModuleService.listAll().then(setAllModules);
    // Migration 060 — catalog names join the suggestions; a failed fetch leaves
    // the tags already in use (never blank).
    void Promise.allSettled([ModuleService.listTags()]).then(([res]) => {
      if (res.status === 'fulfilled') setTagCatalog(res.value);
      else console.error('[ModuleBuilder] tag catalog failed to load', res.reason);
    });
  }, []);
  useTableSync(['module_categories'], () => void CategoriesService.listAll().then(setCategories));
  const tagSuggestions = React.useMemo(
    () => tagSuggestionsFrom(tagCatalog, allModules),
    [tagCatalog, allModules]
  );

  const [blocks, setBlocks] = React.useState<ContentBlock[]>(() => {
    const chunks = draft.seedChunks?.length
      ? draft.seedChunks
      : hasPermission(userPermissions, 'modules.edit')
        ? moduleSeedChunks(draft.module?.chunks, draft.module?.draftChunks)
        : draft.module?.chunks || [];
    const seeded = moduleBlocksFromChunks(chunks);
    return seeded.length ? seeded : blankModuleBlocks();
  });
  const [editor, setEditor] = React.useState<Editor | null>(null);
  const [dirty, setDirty] = React.useState(!!draft.seedChunks?.length);
  const [saving, setSaving] = React.useState(false);
  const [confirmLeave, setConfirmLeave] = React.useState(false);
  // ── Version history (Cohesion R1 Job B1) ── one array feeds both the
  // Versions dialog (list + revert) and the Edit-history dropdown — every
  // archived version IS an edit event, so there is no second feed to keep
  // in sync with this one.
  const [versions, setVersions] = React.useState<ModuleVersion[]>([]);
  const [versionsOpen, setVersionsOpen] = React.useState(false);
  const [versionsLoading, setVersionsLoading] = React.useState(false);
  const toolbarRef = React.useRef<HTMLDivElement>(null);
  // Frozen for the life of this builder. The canvas rebuilds its editor when
  // `docKey` moves, and a brand-new module gets an id on its FIRST save —
  // which must not throw away the undo history of the thing just saved.
  const docKeyRef = React.useRef(`module:${draft.module?.id || `new-${Date.now()}`}`);

  // ─── Permissions (catalog: src/shared/permissions.ts) ───
  const canCreate = hasPermission(userPermissions, 'modules.create');
  const canEditModules = hasPermission(userPermissions, 'modules.edit');
  // Mastering an existing row is `modules.edit`; minting a new one is
  // `modules.create`. The server enforces both through
  // TABLE_PERMS.module_library — a disabled button is a courtesy.
  const mayWrite = baseline ? canEditModules : canCreate;
  const canViewModules = hasPermission(userPermissions, 'modules.view');
  // Publish is its own key (new — the catalog entry lands with the server
  // branch); the server enforces it on `module_publish`.
  const canPublishKey = hasPermission(userPermissions, 'modules.publish');

  // ─── The shared draft (Sync D14) ───
  const blankBlocksFn = React.useCallback(() => blankModuleBlocks(), []);
  const draftApi = useModuleDraft({
    moduleId: baseline?.id ?? null,
    enabled: canEditModules,
    userName,
    toast,
    onBaseline: setBaseline,
    blankBlocks: blankBlocksFn,
  });
  // Until the shared document is bound the sheet shows what the server holds
  // (draft if there is one) — read-only; once bound the document IS the content.
  const { active: draftActive, loadState: draftLoad, initialBlocks: draftInitial } = draftApi;
  const draftCollab = draftApi.collab;
  React.useEffect(() => {
    if (draftActive && draftLoad === 'ready' && !draftCollab) setBlocks(draftInitial);
  }, [draftActive, draftLoad, draftCollab, draftInitial]);
  // On bind, take the live document's blocks so the structure flags are true.
  const readDraftBlocks = draftApi.readBlocks;
  React.useEffect(() => {
    if (draftCollab) setBlocks(readDraftBlocks());
  }, [draftCollab, readDraftBlocks]);

  // ── Load version history whenever the saved module changes underneath us
  // (open a different module, or this one just got its first save) — a
  // brand-new, never-saved module has no id yet and no history to fetch. ──
  React.useEffect(() => {
    if (!baseline?.id || !canViewModules) {
      setVersions([]);
      return;
    }
    let live = true;
    setVersionsLoading(true);
    ModuleService.getVersions(baseline.id)
      .then((rows) => {
        if (live) setVersions(rows);
      })
      .catch((err) => reportError('ModuleBuilder:getVersions', err))
      .finally(() => {
        if (live) setVersionsLoading(false);
      });
    return () => {
      live = false;
    };
  }, [baseline?.id, baseline?.version, canViewModules]);

  // EditorHistoryPanel (shared kit component, same one Studio/forma-studio
  // use) wants { userName, action, timestamp } — every archived version
  // already carries that, so this is a plain reshape, not a second data
  // source.
  const editHistoryEntries = React.useMemo(
    () =>
      versions.map((v) => ({
        userName: v.createdBy || 'Unknown',
        action: v.version <= 1 ? 'created' : 'edited',
        timestamp: v.createdAt || '',
      })),
    [versions]
  );

  const [loadVersionTarget, setLoadVersionTarget] = React.useState<ModuleVersion | null>(null);
  const revertToVersion = React.useCallback(
    (v: ModuleVersion) => {
      setVersionsOpen(false);
      if (draftApi.active && draftApi.collab) {
        // Into the shared draft — everyone in it sees it; Publish makes it current.
        draftApi.loadChunks(v.chunks || []);
        toast(
          `Loaded v${v.version} into the draft — Publish to make it the module's current version`
        );
        return;
      }
      const seeded = moduleBlocksFromChunks(v.chunks || []);
      setBlocks(seeded.length ? seeded : blankModuleBlocks());
      setDirty(true);
      toast(`Loaded v${v.version} — save to make it the module's current version`);
    },
    [toast, draftApi]
  );

  // ─── The structure law ───
  const violationList = React.useMemo(() => validateModuleStructure(blocks), [blocks]);
  // Keyed by WHAT the flags say, not by the array's identity: the canvas
  // repaints on this, and re-running that on every keystroke of a document
  // that is already valid would be work for nothing.
  const violationKey = violationList.map((v) => `${v.blockId}:${v.kind}`).join('|');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const violations = React.useMemo(() => moduleViolationsByBlock(violationList), [violationKey]);
  const moduleBuild = React.useMemo(() => ({ violations }), [violations]);
  const hasPartHeading = React.useMemo(() => blocks.some((b) => (b.level || 1) === 1), [blocks]);

  const trimmed = name.trim();
  const valid = violationList.length === 0;
  // Details = name / description / category / tags: not part of the draft, so
  // they keep a direct save that never moves the version.
  const nameMoved =
    !!baseline &&
    (trimmed !== (baseline.name || '') ||
      (description.trim() || '') !== (baseline.description || ''));
  const categoryMoved = !!baseline && categoryId !== (baseline.categoryId ?? null);
  const tagsMoved = !!baseline && JSON.stringify(tags) !== JSON.stringify(baseline.tags || []);
  const metaMoved = nameMoved || categoryMoved || tagsMoved;
  // A NEW module saves once (create); an existing one saves its details.
  const canSave = baseline
    ? mayWrite && metaMoved && !saving && trimmed.length > 0
    : mayWrite && valid && dirty && !saving && trimmed.length > 0;
  const [publishing, setPublishing] = React.useState(false);
  const [publishOpen, setPublishOpen] = React.useState(false);
  // Publish first WALKS the draft against the published module (SYNC D3/D14).
  const pubWalk = usePublishWalk(editor);
  const { walk } = pubWalk;
  const startPublishWalk = React.useCallback(() => {
    pubWalk.start(draftApi.readPublished(), blocks, () => setPublishOpen(true));
  }, [pubWalk, draftApi, blocks]);
  const [publishNote, setPublishNote] = React.useState('');
  const [discardOpen, setDiscardOpen] = React.useState(false);
  const canPublish = canPublishModule({
    mayPublish: canPublishKey && canEditModules && !!baseline,
    valid,
    pending: draftApi.pending,
    ready: !!draftApi.collab,
    busy: publishing,
    hasName: trimmed.length > 0,
  });

  // ─── Undo, on the app's one timeline (same wiring as the Studio) ───
  const undoTimeline = useUndoCoordinator();
  React.useEffect(() => {
    if (!editor) return;
    undoTimeline.register('doc', {
      undo: () => editor.chain().focus().undo().run(),
      redo: () => editor.chain().focus().redo().run(),
    });
    let depth = undoDepth(editor.state);
    const onTx = () => {
      const next = undoDepth(editor.state);
      if (next > depth) undoTimeline.note('doc');
      depth = next;
    };
    editor.on('transaction', onTx);
    return () => {
      editor.off('transaction', onTx);
      undoTimeline.register('doc', null);
    };
  }, [editor, undoTimeline]);

  const onCanvasChange = React.useCallback((next: ContentBlock[], _doc: JSONContent) => {
    setBlocks(next);
    setDirty(true);
  }, []);

  // ─── Save ───
  // New module: the one create. Existing module: the DETAILS (the definition
  // is the shared draft — it saves itself and moves only on Publish).
  const writeMeta = React.useCallback(async () => {
    if (!baseline) return;
    if (nameMoved) await ModuleService.rename(baseline.id, trimmed, description, userName);
    if (categoryMoved || tagsMoved) {
      await ModuleService.updateMeta(
        baseline.id,
        { ...(categoryMoved ? { categoryId } : {}), ...(tagsMoved ? { tags } : {}) },
        userName
      );
      if (tagsMoved) ensureTagsInCatalog(tags, userName);
    }
  }, [
    baseline,
    nameMoved,
    categoryMoved,
    tagsMoved,
    trimmed,
    description,
    categoryId,
    tags,
    userName,
  ]);

  const doSave = React.useCallback(async () => {
    // Ctrl+S on a shared draft: a silent flush of the mirror.
    if (baseline && draftApi.active && !metaMoved) {
      await draftApi.flush();
      return;
    }
    if (!canSave) return;
    setSaving(true);
    try {
      const nowIso = new Date().toISOString();

      if (!baseline) {
        const chunks = moduleChunksFromBlocks(blocks);
        const created: SpecModule = {
          id: mintId(),
          name: trimmed,
          description: description.trim() || undefined,
          chunks,
          version: 1,
          categoryId: categoryId || undefined,
          // Always a real array (never `undefined`) — an absent `tags` key
          // round-trips to `null` through cloudCache's generic rowToSnake,
          // and the server's tag validator correctly rejects a bare `null`
          // as "not an array" (it can't tell "empty" from "untouched" the
          // way it can for a genuinely-omitted key).
          tags,
          sourceSpecId: draft.sourceSpecId,
          sourceCsi: draft.sourceCsi,
          archivedAt: null,
          createdBy: userName,
          createdAt: nowIso,
          modifiedBy: userName,
          lastModified: nowIso,
        };
        await ModuleService.create(created);
        ensureTagsInCatalog(tags, userName);
        toast(`Saved “${trimmed}” to the module library`);
        setBaseline(created);
        setDirty(false);
        onSaved?.(created);
        return;
      }

      await writeMeta();
      const renamed: SpecModule = {
        ...baseline,
        name: trimmed,
        description: description.trim() || undefined,
        categoryId,
        tags,
        modifiedBy: userName,
        lastModified: nowIso,
      };
      toast(`Saved the details of “${trimmed}”`);
      setBaseline(renamed);
      setDirty(false);
      onSaved?.(renamed);
    } catch (err) {
      reportError('ModuleBuilder:save', err);
      toast(
        err instanceof Error && err.message ? err.message : 'That module could not be saved',
        true
      );
    } finally {
      setSaving(false);
    }
  }, [
    canSave,
    metaMoved,
    draftApi,
    writeMeta,
    blocks,
    baseline,
    trimmed,
    description,
    categoryId,
    tags,
    draft,
    userName,
    toast,
    onSaved,
  ]);

  // ─── Publish (modules.publish): the server takes its copy of the draft as
  // the new definition, bumps the version, writes the version row, clears the
  // draft and raises the update reviews. The client raises nothing. ───
  const doPublish = React.useCallback(async () => {
    if (!baseline || !canPublish) return;
    setPublishing(true);
    try {
      if (metaMoved) await writeMeta();
      const res = await draftApi.publish(publishNote);
      const v = res.version ?? (baseline.version ?? 1) + 1;
      toast(`Published v${v}`);
      setPublishOpen(false);
      setPublishNote('');
      setDirty(false);
      const fresh = await ModuleService.get(baseline.id);
      if (fresh) onSaved?.(fresh);
    } catch (err) {
      reportError('ModuleBuilder:publish', err);
      toast(
        err instanceof Error && err.message ? err.message : 'That module could not be published',
        true
      );
    } finally {
      setPublishing(false);
    }
  }, [baseline, canPublish, metaMoved, writeMeta, draftApi, publishNote, toast, onSaved]);

  const doDiscardDraft = React.useCallback(async () => {
    setDiscardOpen(false);
    if (await draftApi.discard()) toast('Draft discarded — back to the published module');
  }, [draftApi, toast]);

  const saveRef = React.useRef(doSave);
  saveRef.current = doSave;
  const handleSave = React.useCallback(() => void saveRef.current(), []);

  // The shared draft is server-held, so leaving never loses it: no confirm
  // for it (the mirror is flushed on the way out). Only work that is NOT in
  // the draft asks — a new module never saved, or details not yet saved.
  const unsavedOutsideDraft = baseline ? metaMoved : dirty;
  const leave = () => {
    if (unsavedOutsideDraft) setConfirmLeave(true);
    else {
      if (draftApi.active) void draftApi.flush();
      onClose();
    }
  };

  // ─── "Save as new module" (Cohesion R1 Phase 2 item 4) — a fresh row in
  // the library, seeded from what's on screen right now. The module being
  // edited is left exactly as it was; this is a starting point, not a
  // replacement. Gated `modules.create` — a duplicate is always a NEW row,
  // whether or not the writer may edit the one it came from. ───
  const [saveAsNewOpen, setSaveAsNewOpen] = React.useState(false);
  const [saveAsNewName, setSaveAsNewName] = React.useState('');
  const [savingAsNew, setSavingAsNew] = React.useState(false);
  const openSaveAsNew = React.useCallback(() => {
    setSaveAsNewName(`${trimmed || 'Untitled module'} (Copy)`);
    setSaveAsNewOpen(true);
  }, [trimmed]);
  const doSaveAsNew = React.useCallback(async () => {
    const newName = saveAsNewName.trim();
    if (!canCreate || !valid || !newName) return;
    setSavingAsNew(true);
    try {
      const chunks = moduleChunksFromBlocks(blocks);
      const nowIso = new Date().toISOString();
      const created: SpecModule = {
        id: mintId(),
        name: newName,
        description: description.trim() || undefined,
        chunks,
        version: 1,
        categoryId: categoryId || undefined,
        tags, // always a real array — see the matching comment in doSave above
        sourceSpecId: draft.sourceSpecId ?? baseline?.sourceSpecId,
        sourceCsi: draft.sourceCsi ?? baseline?.sourceCsi,
        archivedAt: null,
        createdBy: userName,
        createdAt: nowIso,
        modifiedBy: userName,
        lastModified: nowIso,
      };
      await ModuleService.create(created);
      ensureTagsInCatalog(created.tags ?? [], userName);
      toast(`Saved “${newName}” to the module library as a new module`);
      // The editor now points at the new copy — the same terms Save As
      // leaves a spec master on.
      setBaseline(created);
      setName(newName);
      setDirty(false);
      setSaveAsNewOpen(false);
      onSaved?.(created);
    } catch (err) {
      reportError('ModuleBuilder:saveAsNew', err);
      toast(
        err instanceof Error && err.message ? err.message : 'That module could not be saved',
        true
      );
    } finally {
      setSavingAsNew(false);
    }
  }, [
    canCreate,
    valid,
    saveAsNewName,
    blocks,
    description,
    categoryId,
    tags,
    draft,
    baseline,
    userName,
    toast,
    onSaved,
  ]);

  // ─── Zoom (Cohesion R1 Phase 2 item 2): the shared ZoomControl, percent
  // units, 50–180%. Ctrl+wheel rides the same canvas handler every dsg-family
  // surface now carries (SpecStudioCanvas's onZoomDelta). Session-only — a
  // module builder is opened and closed, not lived in like the Studio. ───
  const [zoomPct, setZoomPct] = React.useState(100);
  const setZoomClamped = React.useCallback((next: number) => {
    setZoomPct(Math.max(ZOOM_MIN_PCT, Math.min(ZOOM_MAX_PCT, Math.round(next))));
  }, []);

  // ─── View: paragraph numbers on/off (Cohesion R1 Phase 2 item 3) — the
  // one item of Studio's View menu that means anything with no variant and no
  // export step to preview. ───
  const [showParagraphNumbers, setShowParagraphNumbers] = React.useState(true);

  // ─── §2.8 panels: click = anchored dropdown, drag = dock or float. The
  // SAME idiom the Studio uses, trimmed to the one palette a module needs
  // (Blocks) — no Outline/Updates/Modules here. ───
  const dockHandleRef = React.useRef<FormaDockHandle | null>(null);
  const [dockEls, setDockEls] = React.useState<Record<string, HTMLElement>>({});
  const [dockPanels, setDockPanels] = React.useState<string[]>([]);
  const [floatingTools, setFloatingTools] = React.useState<FloatingTool[]>([]);
  const [dropdown, setDropdown] = React.useState<{
    id: string;
    anchor: { left: number; bottom: number };
  } | null>(null);
  const [, setPanelDragId] = React.useState<string | null>(null);

  React.useEffect(() => {
    setDockPanels(Object.keys(dockEls));
  }, [dockEls]);

  React.useEffect(() => {
    setFloatingTools((f) => {
      const next = f.filter((t) => !dockPanels.includes(t.id));
      return next.length === f.length ? f : next;
    });
    setDropdown((d) => (d && dockPanels.includes(d.id) ? null : d));
  }, [dockPanels]);

  const floatTool = React.useCallback((id: string, at?: { left: number; top: number } | null) => {
    setDropdown((d) => (d?.id === id ? null : d));
    setFloatingTools((f) => (f.some((t) => t.id === id) ? f : [...f, { id, at }]));
  }, []);
  const closeFloating = React.useCallback(
    (id: string) => setFloatingTools((f) => f.filter((t) => t.id !== id)),
    []
  );
  const dockFloating = React.useCallback((id: string) => {
    setFloatingTools((f) => f.filter((t) => t.id !== id));
    dockHandleRef.current?.showPanel(id);
  }, []);
  const onPanelClick = React.useCallback(
    (id: string, anchor: { left: number; bottom: number }) => {
      if (dockPanels.includes(id)) {
        dockHandleRef.current?.showPanel(id);
        return;
      }
      if (floatingTools.some((t) => t.id === id)) {
        closeFloating(id);
        return;
      }
      setDropdown((d) => (d?.id === id ? null : { id, anchor }));
    },
    [dockPanels, floatingTools, closeFloating]
  );
  const onPanelDragEnd = React.useCallback(
    (id: string, x: number, y: number) => {
      setPanelDragId(null);
      if (isOverDockZone(x, y)) return;
      floatTool(id, { left: x, top: y });
    },
    [floatTool]
  );

  const [dockLocked, setDockLocked] = usePref<boolean>(
    'sf.moduleBuilderDockLocked',
    'sf.moduleBuilderDockLocked',
    true,
    { parse: (raw) => raw !== '0', serialize: (v) => (v ? '1' : '0') }
  );
  const toggleDockLocked = React.useCallback(() => setDockLocked((v) => !v), [setDockLocked]);

  const panelEntries = React.useMemo<PanelEntry[]>(
    () =>
      MODULE_PALETTES.filter((p) => p.id !== 'canvas').map((p) => ({
        id: p.id,
        title: p.title,
        icon: SPEC_STUDIO_PANEL_ICONS[p.id] ?? null,
        where: dockPanels.includes(p.id)
          ? 'docked'
          : floatingTools.some((t) => t.id === p.id)
            ? 'floating'
            : dropdown?.id === p.id
              ? 'dropdown'
              : null,
      })),
    [dockPanels, floatingTools, dropdown]
  );

  const renderPalette = React.useCallback(
    (id: string): React.ReactNode => {
      if (id === 'blocks') return <BlocksPalette editor={editor} canEdit={mayWrite} moduleMode />;
      return null;
    },
    [editor, mayWrite]
  );

  // ─── Toolbar: the Studio's own clusters, nothing bespoke ───
  const toolbarItems = React.useMemo<ToolbarItem[]>(
    () => [
      {
        id: 'undo-redo',
        label: 'Undo and redo',
        core: true,
        section: 'editing',
        node: <UniversalHistory timeline={undoTimeline} editor={editor} />,
      },
      {
        id: 'level',
        label: 'Paragraph level',
        core: true,
        section: 'editing',
        node: <LevelCluster editor={editor} canEdit={mayWrite} />,
      },
      {
        id: 'marks',
        label: 'Text formatting',
        section: 'editing',
        node: <InlineMarks editor={editor} canEdit={mayWrite} />,
      },
      {
        id: 'zoom',
        label: 'Zoom',
        core: true,
        section: 'editing',
        node: <ZoomControl value={zoomPct} onChange={setZoomClamped} />,
      },
      {
        id: 'paragraph-numbers',
        label: 'Paragraph numbers',
        section: 'editing',
        // CORE for the same reason zoom is: this toolbar had almost no items
        // before Phase 2, so a pin list saved against the old shape would
        // otherwise hide a brand-new control from anyone who customised early.
        core: true,
        node: (
          <ToolbarButton
            label="Paragraph numbers"
            active={showParagraphNumbers}
            onClick={() => setShowParagraphNumbers((v) => !v)}
            tooltip="Show or hide paragraph numbers on this sheet"
          />
        ),
      },
      {
        id: 'save-as-new-module',
        label: 'Save as new module',
        core: true,
        section: 'document',
        node: (
          <ToolbarButton
            label="Save as new module"
            variant="default"
            disabled={!canCreate}
            onClick={openSaveAsNew}
            tooltip={
              canCreate
                ? 'Copy this module into the library as a new starting point'
                : 'Saving new modules needs the Create Modules privilege'
            }
          />
        ),
      },
      ...(draftApi.active
        ? [
            {
              id: 'discard-draft',
              label: 'Discard draft',
              section: 'document' as const,
              node: (
                <ToolbarButton
                  label="Discard draft"
                  variant="outline"
                  disabled={!canEditModules || !draftApi.pending}
                  onClick={() => setDiscardOpen(true)}
                  tooltip="Drop the shared draft for everyone and go back to the published module"
                />
              ),
            },
          ]
        : []),
      ...(canViewModules && baseline
        ? [
            {
              id: 'versions',
              label: 'Version history',
              section: 'document' as const,
              node: (
                <ToolbarButton
                  label="Versions"
                  variant="outline"
                  onClick={() => setVersionsOpen(true)}
                />
              ),
            },
            {
              id: 'history',
              label: 'Edit history',
              section: 'document' as const,
              node: <EditorHistoryPanel entries={editHistoryEntries} />,
            },
          ]
        : []),
    ],
    [
      undoTimeline,
      editor,
      mayWrite,
      zoomPct,
      setZoomClamped,
      showParagraphNumbers,
      canCreate,
      openSaveAsNew,
      canViewModules,
      canEditModules,
      baseline,
      editHistoryEntries,
      draftApi.active,
      draftApi.pending,
    ]
  );

  const version = baseline?.version ?? 1;

  return (
    <div
      className={[
        'dsg-root',
        'sps-root',
        'sps-mod-builder',
        showParagraphNumbers ? '' : 'sps-no-numbers',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <header className="dsg-topbar">
        <div className="dsg-idrow">
          <button type="button" className="dsg-back" onClick={leave}>
            <Icon icon={ArrowLeft} em={1.1538} aria-hidden="true" />
            Modules
          </button>
          <nav className="dsg-crumb" aria-label="Breadcrumb">
            <span className="dsg-crumb-project">Module</span>
            <span className="dsg-crumb-sep" aria-hidden="true">
              ▸
            </span>
            <span className="dsg-crumb-spec">
              <strong>{trimmed || 'Untitled module'}</strong>
            </span>
          </nav>
          <span className="sps-chip">{baseline ? `v${version}` : 'NEW'}</span>
          {draft.sourceCsi && !baseline && <span className="sps-chip">from {draft.sourceCsi}</span>}
          <div className="dsg-idrow-status">
            {draftApi.active && <ModuleDraftChip state={draftApi.chip} />}
            {draftApi.active && <PeerChips peers={draftApi.peers} />}
            {draftApi.liveBlocked && (
              <span className="sps-mod-count" role="status">
                {liveBlockMessage(draftApi.liveBlocked)}
              </span>
            )}
            {!valid && (
              <span className="sps-mod-count" role="status">
                <Icon icon={AlertTriangle} em={1.1818} aria-hidden="true" />
                {violationList.length} to fix before {baseline ? 'publishing' : 'saving'}
              </span>
            )}
          </div>
        </div>

        <div ref={toolbarRef}>
          <DesignerToolbar
            items={toolbarItems}
            panels={panelEntries}
            onPanelClick={onPanelClick}
            onPanelDragStart={setPanelDragId}
            onPanelDragEnd={onPanelDragEnd}
            dockLocked={dockLocked}
            onToggleDockLocked={toggleDockLocked}
            onResetLayout={() => dockHandleRef.current?.resetLayout()}
            userId={userId || userName}
            storageKeyPrefix="sf.moduleBuilderToolbar.v1."
          />
        </div>
      </header>

      {walk && (
        <ReviewBar
          lane="master"
          mode="walk"
          index={pubWalk.walker.index}
          total={walk.length}
          onPrev={pubWalk.walker.prev}
          onNext={pubWalk.walker.next}
          onFinish={() => pubWalk.finish(() => setPublishOpen(true))}
          onLeave={pubWalk.leave}
          canDecide={false}
          busy={publishing}
          note="Your changes since the published version"
        />
      )}

      <div className="sps-mod-meta">
        <div className="sps-mod-meta-field">
          <Label htmlFor="sf-mod-name">Name</Label>
          <Input
            id="sf-mod-name"
            value={name}
            placeholder="e.g. Gypsum board basis of design"
            disabled={!mayWrite}
            onChange={(e) => {
              setName(e.target.value);
              setDirty(true);
            }}
          />
        </div>
        <div className="sps-mod-meta-field">
          <Label htmlFor="sf-mod-desc">Description (optional)</Label>
          <Input
            id="sf-mod-desc"
            value={description}
            placeholder="When should somebody reach for this?"
            disabled={!mayWrite}
            onChange={(e) => {
              setDescription(e.target.value);
              setDirty(true);
            }}
          />
        </div>
        <div className="sps-mod-meta-field">
          <Label htmlFor="sf-mod-category">Category</Label>
          <ModuleCategorySelect
            value={categoryId}
            onChange={(id) => {
              setCategoryId(id);
              setDirty(true);
            }}
            categories={categories}
            canCreate={canCreate}
            userName={userName}
            disabled={!mayWrite}
            ariaLabel="Category"
            describedBy={metaError ? 'sf-mod-meta-error' : undefined}
            onError={setMetaError}
          />
        </div>
        <div className="sps-mod-meta-field">
          <Label htmlFor="sf-mod-tags">Tags</Label>
          <ModuleTagsInput
            id="sf-mod-tags"
            value={tags}
            onChange={(next) => {
              setTags(next);
              setDirty(true);
            }}
            suggestions={tagSuggestions}
            disabled={!mayWrite}
            ariaLabel="Tags"
          />
        </div>
        {metaError && (
          <p
            id="sf-mod-meta-error"
            role="alert"
            aria-live="assertive"
            style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)', margin: 0 }}
          >
            {metaError}
          </p>
        )}

        {/* Ruling 1, said once and quietly. */}
        <p className="sps-mod-note">
          {baseline
            ? 'Edits here are a draft shared with anyone else in this module. Publishing changes the module for everyone: new inserts get it, and every spec already using it is offered the update.'
            : 'Saving adds this module to the library.'}{' '}
          Editing a module inside a spec only changes that spec’s copy.
          {hasPartHeading && ' Part headings are not saved — the part on each article places it.'}
        </p>

        {baseline ? (
          <>
            <Button variant="outline" onClick={handleSave} disabled={!canSave}>
              {saving ? 'Saving…' : 'Save details'}
            </Button>
            <Button
              onClick={startPublishWalk}
              disabled={!canPublish || !!walk}
              className="sps-save-btn"
              title={
                canPublishKey
                  ? draftApi.pending
                    ? `Publish the draft as v${version + 1}`
                    : 'Nothing to publish — the draft matches the published module'
                  : 'Publishing needs the Publish Modules privilege'
              }
            >
              {publishing ? 'Publishing…' : 'Publish'}
            </Button>
          </>
        ) : (
          <Button onClick={handleSave} disabled={!canSave} className="sps-save-btn">
            {saving ? 'Saving…' : 'Save module'}
          </Button>
        )}
      </div>

      <div className="dsg-body">
        <main className="dsg-main">
          <div className="dsg-workspace sps-workspace">
            <FormaDock
              isDark={isDark}
              locked={dockLocked}
              palettes={MODULE_PALETTES}
              layoutKey={`sf.moduleBuilderDock.v1.${userId || userName || 'anon'}`}
              panelIcons={SPEC_STUDIO_PANEL_ICONS}
              onContainersChange={setDockEls}
              onReady={(h) => {
                dockHandleRef.current = h;
              }}
            />

            {dockEls.canvas &&
              createPortal(
                <SpecStudioCanvas
                  docKey={docKeyRef.current}
                  blocks={blocks}
                  specNumber=""
                  specName={trimmed || 'Untitled module'}
                  zoom={zoomPct / 100}
                  collab={draftApi.active ? draftApi.collab : null}
                  // Editing waits for the shared document (synced + seeded), so
                  // nothing typed can land outside it.
                  editable={mayWrite && !walk && (!draftApi.active || !!draftApi.collab)}
                  canDelete={mayWrite && (!draftApi.active || !!draftApi.collab)}
                  onChange={onCanvasChange}
                  onSave={handleSave}
                  onEditorReady={setEditor}
                  onZoomDelta={(step) => setZoomClamped(zoomPct + step * 100)}
                  moduleBuild={moduleBuild}
                />,
                dockEls.canvas
              )}

            {MODULE_PALETTES.filter((p) => p.id !== 'canvas').map((p) =>
              dockEls[p.id] ? (
                <React.Fragment key={p.id}>
                  {createPortal(renderPalette(p.id), dockEls[p.id])}
                </React.Fragment>
              ) : null
            )}

            {/* Floating panels — the SAME bodies, no dock required. */}
            {floatingTools
              .filter((f) => !dockEls[f.id])
              .map((f, i) => (
                <ToolPanel
                  key={f.id}
                  title={titleOf(f.id)}
                  index={i}
                  at={f.at}
                  onDock={() => dockFloating(f.id)}
                  onClose={() => closeFloating(f.id)}
                >
                  {renderPalette(f.id)}
                </ToolPanel>
              ))}

            {/* The panel dropped down from its toolbar button (§2.8). */}
            {dropdown && !dockEls[dropdown.id] && (
              <PanelDropdown
                title={titleOf(dropdown.id)}
                anchor={dropdown.anchor}
                onFloat={() =>
                  floatTool(dropdown.id, {
                    left: dropdown.anchor.left + 180,
                    top: dropdown.anchor.bottom + 14,
                  })
                }
                onClose={() => setDropdown(null)}
              >
                {renderPalette(dropdown.id)}
              </PanelDropdown>
            )}
          </div>
        </main>
        {walk && (
          <ReviewPanel
            title="Changes in this draft"
            items={walk}
            currentId={pubWalk.walker.current?.id ?? null}
            onJump={pubWalk.walker.setCurrentId}
            invert
            showDecisions={false}
            emptyTitle="This draft matches the published version."
          />
        )}
      </div>

      {editor && <FindReplace editor={editor} canEdit={mayWrite} anchorRef={toolbarRef} />}

      {/* ── Publish — names who else is in the draft ── */}
      <Dialog open={publishOpen} onOpenChange={(o) => !publishing && setPublishOpen(o)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Publish v{version + 1}?</DialogTitle>
            <DialogDescription>{modulePublishBody(draftApi.peers, version + 1)}</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
            <Label htmlFor="sf-mod-publish-note">Note (optional)</Label>
            <Input
              id="sf-mod-publish-note"
              value={publishNote}
              placeholder="What changed?"
              onChange={(e) => setPublishNote(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishOpen(false)} disabled={publishing}>
              Cancel
            </Button>
            <Button onClick={() => void doPublish()} disabled={!canPublish}>
              {publishing ? 'Publishing…' : 'Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title="Discard this draft?"
        description={moduleDiscardBody(draftApi.peers)}
        confirmLabel="Discard draft"
        destructive
        onConfirm={() => void doDiscardDraft()}
      />

      {/* ── Save as new module ── */}
      <Dialog open={saveAsNewOpen} onOpenChange={(o) => !savingAsNew && setSaveAsNewOpen(o)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Save as new module</DialogTitle>
            <DialogDescription>
              Copies this module into the library as a new starting point. This module is left
              untouched.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'grid', gap: 'var(--space-1)' }}>
            <Label htmlFor="sf-mod-saveas-name">Name</Label>
            <Input
              id="sf-mod-saveas-name"
              value={saveAsNewName}
              autoFocus
              onChange={(e) => setSaveAsNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canCreate && valid && saveAsNewName.trim()) {
                  void doSaveAsNew();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSaveAsNewOpen(false)}
              disabled={savingAsNew}
            >
              Cancel
            </Button>
            <Button
              onClick={() => void doSaveAsNew()}
              disabled={!canCreate || !valid || !saveAsNewName.trim() || savingAsNew}
            >
              {savingAsNew ? 'Saving…' : 'Save as new module'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Version history + revert (Cohesion R1 Job B1) — same copy and
             layout as the spec master's Versions dialog (Studio.tsx). ── */}
      <Dialog open={versionsOpen} onOpenChange={setVersionsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Version history</DialogTitle>
            <DialogDescription>
              {baseline ? `v${baseline.version ?? 1} — ${versions.length} archived` : ''}
            </DialogDescription>
          </DialogHeader>
          {versionsLoading ? (
            <p className="sps-empty-line">Loading…</p>
          ) : versions.length === 0 ? (
            <p className="sps-empty-line">No previous versions yet.</p>
          ) : (
            <div className="sps-version-list">
              {versions.map((v) => (
                <div key={v.id} className="sps-version-row">
                  <div className="sps-version-meta">
                    <span className="sps-version-name">v{v.version}</span>
                    <span className="sps-version-sub">
                      by {v.createdBy || 'Unknown'}
                      {v.createdAt
                        ? ` — ${new Date(v.createdAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}`
                        : ''}{' '}
                      · {(v.chunks || []).length} block runs
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!canEditModules}
                    title={
                      canEditModules
                        ? `Load version ${v.version} into the editor`
                        : 'Reverting needs the Edit Modules privilege'
                    }
                    onClick={() => setLoadVersionTarget(v)}
                  >
                    Revert
                  </Button>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button type="button" onClick={() => setVersionsOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Exit guard — Keep editing / Discard / Save and leave, the same
             three-way choice the Studio's own guard offers (Studio.tsx). ── */}
      <Dialog open={confirmLeave} onOpenChange={(o) => !o && setConfirmLeave(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>You have unsaved changes</DialogTitle>
            <DialogDescription>
              {baseline
                ? 'The details of this module have not been saved. The draft itself is already saved.'
                : 'Save this module, discard the changes, or stay here and keep working.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmLeave(false)}>
              Keep editing
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setConfirmLeave(false);
                onClose();
              }}
            >
              Discard
            </Button>
            <Button
              disabled={!canSave}
              onClick={() => {
                setConfirmLeave(false);
                void doSave().then(() => onClose());
              }}
            >
              {saving ? 'Saving…' : baseline ? 'Save details and leave' : 'Save and leave'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!loadVersionTarget}
        onOpenChange={(o) => !o && setLoadVersionTarget(null)}
        title={`Load version ${loadVersionTarget?.version ?? ''}?`}
        description={
          draftApi.active
            ? `Load version ${loadVersionTarget?.version ?? ''} into the shared draft? It replaces what is in the draft now, for everyone in it. Nothing is published until you Publish.`
            : `Load version ${loadVersionTarget?.version ?? ''} into the editor? Unsaved changes here are lost.`
        }
        confirmLabel="Load version"
        onConfirm={() => {
          const v = loadVersionTarget;
          setLoadVersionTarget(null);
          if (v) revertToVersion(v);
        }}
      />
    </div>
  );
}

export default ModuleBuilder;
